import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from 'react';
import {Search, ChevronDown, X} from 'lucide-react';
import cn from 'classnames';
import {useFlowData} from '../../EditProcess/hooks/useFlowData';
import {
  findParentByChild,
  formatLabel,
  getChildFields,
  getParentFields,
} from '../../EditProcess/utils';
import {CreditCheckContext} from '../../../../../helpers/context/CreditCheckContext';
import {EasyDetectContext} from '../../../../../helpers/context/EasyDetectContext';

function NestedDropdown({
  isLoading,
  handleUpdateCondition,
  condition,
  cleanField,
}) {
  const FLowData = useFlowData();
  const {aggregateFunction} = useContext(EasyDetectContext);
  const {
    get: {currentFlowData},
  } = React.useContext(CreditCheckContext);
  const data = React.useMemo(() => {
    if (!isLoading && FLowData.fieldsAndOperators) {
      return FLowData.fieldsAndOperators.data;
    }
  }, [FLowData.fieldsAndOperators, isLoading]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selection, setSelection] = useState({
    parent: null,
    child: null,
  });
  const [expandedParents, setExpandedParents] = useState(new Set());
  const dropdownRef = useRef(null);
  const parentOptions = useMemo(
    () => getParentFields(data, currentFlowData?.usecase),
    [data, currentFlowData?.usecase],
  );

  const filteredData = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    if (!lowerSearchTerm) {
      return parentOptions.map(parent => ({
        ...parent,
        children: getChildFields(parent.value, data),
      }));
    }

    const results = [];
    parentOptions.forEach(parent => {
      const parentLabelMatch = parent.label
        .toLowerCase()
        .includes(lowerSearchTerm);
      const children = getChildFields(parent.value, data);
      const matchingChildren = children.filter(child =>
        child.label.toLowerCase().includes(lowerSearchTerm),
      );

      if (parentLabelMatch || matchingChildren.length > 0) {
        results.push({
          ...parent,
          children: parentLabelMatch ? children : matchingChildren,
          parentMatch: parentLabelMatch,
        });
      }
    });
    return results;
  }, [searchTerm, parentOptions, data]);

  const handleParentToggle = (parentValue, event) => {
    event.stopPropagation();
    setExpandedParents(prev => {
      // Check if the clicked parent is already the one expanded
      const isAlreadyExpanded = prev.has(parentValue);
      // If it's already expanded, clicking again collapses it (empty Set).
      // If it's not expanded, clicking expands it and collapses others (Set with only this parent).
      return isAlreadyExpanded ? new Set() : new Set([parentValue]);
    });
  };

  // Handles selecting a child item
  const handleChildSelect = (parent, child, event) => {
    event.stopPropagation();
    setSelection({parent, child});
    handleUpdateCondition('parent', parent.value || '');

    const childValue =
      parent.value === 'aggregate_functions'
        ? `${child.value}_${aggregateFunction}`
        : child.value;

    handleUpdateCondition('field', childValue || '');
    setIsOpen(false);
    setExpandedParents(new Set());
    setSearchTerm('');
  };

  // --- Effects ---
  // Effect to handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setExpandedParents(new Set()); // Collapse parents on close
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Effect to expand parents that contain matching children when search term changes
  useEffect(() => {
    if (searchTerm) {
      const parentsToExpand = new Set();
      filteredData.forEach(parent => {
        // Expand if the parent didn't match but children did
        if (!parent.parentMatch && parent.children.length > 0) {
          parentsToExpand.add(parent.value);
        }
      });
      setExpandedParents(parentsToExpand);
    } else {
      // Optionally collapse all when search is cleared, or keep user's expansions
      setExpandedParents(new Set()); // Uncomment to collapse all when search clears
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filteredData]); // Rerun when search or filtered results change

  const findParentFields = useCallback(
    childValue => findParentByChild(childValue, data),
    [data],
  );

  // --- Display Logic ---

  // Generates the display text for the main trigger button
  const getDisplayText = useCallback(() => {
    const {parent, child} = selection;
    if ((child && parent) || cleanField) {
      const foundParent = findParentFields(cleanField);
      return `${parent?.label || foundParent?.value} → ${
        child?.label || condition?.field
      }`;
    } else if (parent) {
      // Allow selecting just a parent if needed, though current logic requires child selection
      return parent.label;
    } else {
      return 'Select a field';
    }
  }, [condition?.field, findParentFields, selection, cleanField]);

  useEffect(() => {
    if (condition.field) {
      getDisplayText();
    }
  }, [condition?.field, getDisplayText]);

  // --- Render ---
  return (
    <div className="relative font-sans" ref={dropdownRef}>
      {/* Main dropdown trigger button */}
      <button
        type="button"
        className={cn(
          'flex items-center justify-between w-full p-3 border border-grey-100 rounded-md cursor-pointer transition-colors duration-200 text-left',

          isOpen
            ? 'border-indigo-500 shadow-sm bg-white'
            : 'border-grey-300 hover:border-grey-400 bg-white',
        )}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex-1 text-sm truncate pr-2 text-body">
          {formatLabel(getDisplayText())}
        </span>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-grey-500 transition-transform duration-200 ease-in-out flex-shrink-0', // Added easing
            isOpen ? 'transform rotate-180' : '',
          )}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown Menu Container - Added animation */}
      <div
        className={cn(
          'absolute z-30 w-full mt-1 bg-white border border-grey-100 rounded-md shadow-lg',
          'transition-all duration-200 ease-out origin-top', // Use transition-all for opacity and transform
          isOpen
            ? 'opacity-100 scale-100' // Open state
            : 'opacity-0 scale-95 pointer-events-none', // Closed state
        )}
        role="listbox"
      >
        {/* Search Box */}
        <div className="p-2 border-b border-grey-100 sticky top-0 bg-white z-10">
          <div className="flex items-center px-2 py-1.5 border border-grey-100 rounded-md  ">
            <Search
              className="w-4 h-4 text-grey-400 mr-2 flex-shrink-0"
              aria-hidden="true"
            />
            <input
              type="text"
              className="w-full py-0 h-6 text-body !outline-none text-sm bg-transparent"
              placeholder="Search fields..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onClick={e => e.stopPropagation()}
              aria-label="Search fields"
            />
            {searchTerm && (
              <button
                type="button"
                className="ml-1 text-body hover:text-grey-600 focus:outline-none"
                onClick={e => {
                  e.stopPropagation();
                  setSearchTerm('');
                }}
                aria-label="Clear search"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {/* Options List */}
        <div className="max-h-72 overflow-y-auto" role="group">
          {filteredData.length > 0 ? (
            filteredData.map((parent, index) => {
              const isExpanded = expandedParents.has(parent.value);
              const isSelectedParent =
                selection.parent?.value === parent.value ||
                parent.value === findParentFields(cleanField)?.value;

              return (
                <div
                  key={parent.value + index}
                  role="presentation"
                  className="border-b border-grey-100 last:border-b-0"
                >
                  {' '}
                  {/* Added subtle border between parents */}
                  {/* Parent Item */}
                  <div
                    className={cn(
                      'flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer  transition-colors duration-150', // Added transition
                      isSelectedParent
                        ? 'bg-brandBlue'
                        : 'bg-white hover:bg-grey-100',
                      // isExpanded && 'bg-white80',
                    )}
                    onClick={e => handleParentToggle(parent.value, e)}
                    role="option"
                    aria-selected={isSelectedParent && !selection.child}
                    aria-expanded={isExpanded} // Indicate expansion state for accessibility
                  >
                    <span
                      className={cn(
                        'font-medium text-body py-1',
                        isSelectedParent ? 'text-white' : 'text-grey-300',
                      )}
                    >
                      {parent.label}
                    </span>
                    {parent.children.length > 0 && (
                      <span
                        className="ml-2 transition-transform duration-300 ease-in-out"
                        style={{
                          transform: isExpanded
                            ? 'rotate(0deg)'
                            : 'rotate(-90deg)',
                        }}
                      >
                        {' '}
                        {/* Animated chevron container */}
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 ',
                            isSelectedParent ? 'text-white' : 'text-grey-500',
                          )}
                          aria-hidden="true"
                        />
                      </span>
                    )}
                  </div>
                  {/* Child Items Container - Added animation */}
                  <div
                    className={cn(
                      'overflow-hidden transition-[max-height] duration-300 ease-in-out', // Max-height transition
                      isExpanded ? 'max-h-[700px]' : 'max-h-0', // Set max-height based on state (adjust max-h value if needed)
                    )}
                  >
                    {/* Inner div prevents margin collapse issues with max-height transition */}
                    <div className=" mx-4 my-1" role="group">
                      {parent.children.map((child, index) => {
                        const isSelectedChild =
                          isSelectedParent &&
                          (selection.child?.value === child.value ||
                            child.value === cleanField);
                        return (
                          <div
                            key={child.value + index}
                            className={cn(
                              'px-4 py-3 bg-white80 text-sm cursor-pointer hover:bg-grey-100 transition-colors duration-150', // Added transition
                              isSelectedChild
                                ? 'text-brandBlue font-bold'
                                : 'text-grey-300 hover:text-grey-900',
                            )}
                            onClick={e => handleChildSelect(parent, child, e)}
                            role="option"
                            aria-selected={isSelectedChild}
                          >
                            {child.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-2.5 text-sm text-grey-500">
              No options found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export {NestedDropdown};
